import React, { useState, useContext } from 'react';
import { Table, Tooltip } from 'reactstrap';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { RoleHelper } from '@shoflo/polymer/helpers/RoleHelper/RoleHelper';
import { shouldHaveFeature } from '@shoflo/polymer/helpers/Feature/Feature';
import NoItems, { ContentType } from 'components/NoItems';
import UserContext from 'containers/contexts/UserContext.js';
import ListItem from './CrewListItem';

const ColumnHeader = styled.th`
  font-size: 16px;
  font-style: normal;
  font-weight: normal;
  color: #767676 !important;
  border: none !important;
  padding: 2rem 0 1rem !important;
  min-width: 220px;

  @media only screen and (max-width: 768px) {
    min-width: auto;
  }
`;

const StyledButtonHeader = styled(ColumnHeader)`
  padding: 0 !important;
  text-align: right;
`;

const StyledTable = styled(Table)`
  table-layout: fixed;

  tbody tr {
    td {
      transition: background 0.2s;
    }
    td div[id*='user_avatar'] {
      transition: transform 0.2s;
    }

    &:hover {
      td {
        background: #f5fbff;

        &:first-child {
          div[id*='user_avatar'] {
            transform: translateX(5px);
          }
        }

        &:last-child {
          button.btn {
            border: 1px solid #767676;
            transform: translateX(-5px);
          }
        }
      }
    }
  }

  @media only screen and (max-width: 768px) {
    table-layout: auto;
  }
`;
const PermissionColumnHeader = styled(ColumnHeader)`
  width: 310px;

  @media only screen and (max-width: 768px) {
    opacity: 0;
    width: 120px;
  }
`;

const CrewList = ({
  teamId,
  eventId,
  inviteCrewButton,
  users,
  hasEventManagementControls,
  onChangeRoleClick,
  onDeleteClick,
  onResendInviteClick,
  isLoading,
  onAvailableToolsChanged,
  team,
  showModal,
}) => {
  const [toggle, setToggle] = useState(false);
  const currentUser = useContext(UserContext);
  const isTeamAdmin = RoleHelper.hasTeamAdminRole(currentUser, teamId);
  const isEventAdmin = RoleHelper.hasEventAdminRole(
    currentUser,
    teamId,
    eventId,
  );

  const changeToggleState = () => setToggle(!toggle);

  const rows = users.map((user) => (
    <ListItem
      key={`crew-table-row-${user._id}`}
      canManage={hasEventManagementControls}
      {...{
        eventId,
        teamId,
        onChangeRoleClick,
        onResendInviteClick,
        onDeleteClick,
        user,
        onAvailableToolsChanged,
        canManage: hasEventManagementControls,
        team,
        isLoggedInTeamAdmin: isTeamAdmin,
        isLoggedInEventAdmin: isEventAdmin,
        showModal,
      }}
    />
  ));

  const redirectToLearnPage = () =>
    window.open(
      'http://learn.studio.cvent.com/en/articles/1067125-crew-permission-levels',
      '_blank',
    );

  return (
    <StyledTable>
      <thead>
        <tr>
          <ColumnHeader>User</ColumnHeader>
          <PermissionColumnHeader>
            <div
              data-testid="tip-permission-tooltip"
              onClick={redirectToLearnPage}
            >
              <Tooltip
                placement="top"
                isOpen={isTeamAdmin && isEventAdmin && toggle}
                autohide={false}
                target="tip-permission"
                toggle={changeToggleState}
                style={{ cursor: 'pointer' }}
              >
                Choose between a Read Only, Contributor, Admin, or Show caller
                permission. Click to learn about more permissions.
              </Tooltip>
            </div>
            <span id="tip-permission" data-testid="tip-permission">
              Permission
            </span>
          </PermissionColumnHeader>
          {shouldHaveFeature('event-crew-tools') && (
            <ColumnHeader>Tools</ColumnHeader>
          )}
          <StyledButtonHeader className="d-none d-lg-table-cell">
            {inviteCrewButton}
          </StyledButtonHeader>
        </tr>
      </thead>
      <tbody>
        {isLoading && !rows.length && (
          <NoItems type={ContentType.Crew} isLoading={isLoading} />
        )}
        {rows}
      </tbody>
    </StyledTable>
  );
};

CrewList.propTypes = {
  teamId: PropTypes.string.isRequired,
  eventId: PropTypes.string.isRequired,
  users: PropTypes.array.isRequired,
  hasEventManagementControls: PropTypes.bool.isRequired,
  onChangeRoleClick: PropTypes.func.isRequired,
  onResendInviteClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  team: PropTypes.object.isRequired,
  showModal: PropTypes.func.isRequired,
};

export default CrewList;
